import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { BookOpen, Video, Target, TrendingUp, Calendar, Sparkles, Plus, LogOut, RefreshCw, Award, Users } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function PlayerDashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, getAuthHeader } = useAuth();
  const { theme } = useTheme();
  const [stats, setStats] = useState(null);
  const [recentJournals, setRecentJournals] = useState([]);
  const [weeklyPulse, setWeeklyPulse] = useState(null);
  const [loadingPulse, setLoadingPulse] = useState(false);
  const [cricclubsStats, setCricclubsStats] = useState(null);
  const [syncingCricClubs, setSyncingCricClubs] = useState(false);
  const [editingCricClubsId, setEditingCricClubsId] = useState(false);
  const [cricclubsIdInput, setCricclubsIdInput] = useState('');
  const [loading, setLoading] = useState(true);
  const [myCoaches, setMyCoaches] = useState([]);

  useEffect(() => {
    // Redirect coaches to coach dashboard
    if (user?.cricket_role === 'Coach') {
      navigate('/coach-dashboard', { replace: true });
      return;
    }
    
    // Always fetch fresh data when dashboard mounts
    fetchDashboard();
    fetchMyCoaches();
    // Don't fetch pulse immediately - let dashboard load first
    setTimeout(() => {
      fetchWeeklyPulse();
    }, 2000);
  }, [user]);

  const fetchMyCoaches = async () => {
    console.log('Fetching my coaches...');
    try {
      const response = await axios.get(`${API}/player/my-coaches`, {
        headers: getAuthHeader()
      });
      console.log('My coaches response:', response.data);
      const coaches = response.data.coaches || [];
      console.log('Setting coaches:', coaches);
      setMyCoaches(coaches);
    } catch (error) {
      console.error('Error fetching coaches:', error);
      setMyCoaches([]);
      // Don't show error toast - just log it
    }
  }; // Empty dependency array - runs once on mount
  
  // Also refetch when returning to dashboard
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        fetchDashboard();
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  useEffect(() => {
    if (user?.cricclubs_id) {
      setCricclubsIdInput(user.cricclubs_id);
    }
  }, [user]);

  useEffect(() => {
    if (location.state?.refresh) {
      fetchDashboard();
      window.history.replaceState({}, document.title);
    }
  }, [location]);

  const fetchDashboard = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/player/dashboard`, {
        headers: getAuthHeader()
      });
      setStats(response.data.stats);
      setRecentJournals(response.data.recent_journals);
      setCricclubsStats(response.data.cricclubs_stats);
    } catch (error) {
      console.error('Dashboard error:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchWeeklyPulse = async () => {
    setLoadingPulse(true);
    try {
      const response = await axios.get(`${API}/player/weekly-pulse`, {
        headers: getAuthHeader(),
        timeout: 15000
      });
      setWeeklyPulse(response.data);
    } catch (error) {
      console.error('Pulse error:', error);
      setWeeklyPulse({
        pulse: "Keep up the great work! Log more sessions to see AI-powered insights.",
        journal_count: 0,
        video_count: 0,
        practice_hours: 0
      });
    } finally {
      setLoadingPulse(false);
    }
  };

  const handleLogout = () => {
    logout();
    toast.success('Logged out successfully');
    navigate('/');
  };

  const syncCricClubsData = async () => {
    setSyncingCricClubs(true);
    try {
      const response = await axios.post(`${API}/player/sync-cricclubs`, {}, {
        headers: getAuthHeader()
      });
      
      setCricclubsStats(response.data.stats);
      
      if (response.data.cached) {
        toast.info('Using cached stats (synced recently)');
      } else {
        toast.success('CricClubs stats synced successfully!');
      }
      
      // Refresh dashboard to get updated player profile
      await fetchDashboard();
      
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Sync failed');
      console.error(error);
    } finally {
      setSyncingCricClubs(false);
    }
  };

  const updateCricClubsId = async () => {
    if (!cricclubsIdInput.trim()) {
      toast.error('Please enter a valid CricClubs ID');
      return;
    }

    try {
      // Save the ID directly
      await axios.put(`${API}/auth/update-profile`, {
        cricclubs_id: cricclubsIdInput
      }, {
        headers: getAuthHeader()
      });

      toast.success('CricClubs ID updated! Now syncing stats...');
      setEditingCricClubsId(false);
      
      // Auto-sync after saving
      await syncCricClubsData();
      
      // Refresh entire dashboard
      await fetchDashboard();
      
    } catch (error) {
      toast.error('Failed to update CricClubs ID');
      console.error(error);
    }
  };

  return (
    <div className="min-h-screen" style={{ 
      background: theme === 'dark' 
        ? 'linear-gradient(135deg, #0a0e27 0%, #1a1f3a 100%)' 
        : 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)'
    }}>
      <div className={`backdrop-blur-md ${theme === 'dark' ? 'bg-white/5' : 'bg-black/5'} border-b ${theme === 'dark' ? 'border-white/10' : 'border-black/10'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className={`text-3xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                Welcome, {user?.full_name}
              </h1>
              <div className="flex items-center space-x-2">
                <p className={`${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                  {user?.cricket_role} • {stats?.community_points || 0} points
                </p>
                {user?.subscription_tier === 'pro' && (
                  <span className="px-3 py-1 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 text-white text-xs font-bold">
                    PRO
                  </span>
                )}
              </div>
            </div>
            <div className="flex items-center space-x-4">
              {user?.subscription_tier === 'free' && (
                <Button onClick={() => navigate('/pricing')} className="bg-gradient-to-r from-purple-500 to-pink-500">
                  Upgrade to Pro
                </Button>
              )}
              <Button variant="outline" onClick={() => navigate('/')}>Home</Button>
              <Button variant="outline" onClick={handleLogout} className="text-red-500 border-red-500 hover:bg-red-500/10">
                <LogOut className="w-4 h-4 mr-2" />
                Sign Out
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Card className={`glass-card text-center ${theme === 'light' ? 'bg-white/95' : ''}`}>
            <div className="text-3xl font-bold gradient-text">{stats?.total_journal_entries || 0}</div>
            <div className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>Journal Entries</div>
          </Card>
          <Card className={`glass-card text-center ${theme === 'light' ? 'bg-white/95' : ''}`}>
            <div className="text-3xl font-bold gradient-text">{stats?.total_videos || 0}</div>
            <div className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>Videos</div>
          </Card>
          <Card className={`glass-card text-center ${theme === 'light' ? 'bg-white/95' : ''}`}>
            <div className="text-3xl font-bold gradient-text">{stats?.total_practice_hours || 0}</div>
            <div className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>Practice Hours</div>
          </Card>
          <Card className={`glass-card text-center ${theme === 'light' ? 'bg-white/95' : ''}`}>
            <div className="text-3xl font-bold text-green-500">{stats?.community_points || 0}</div>
            <div className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>Points</div>
          </Card>
        </div>

        {weeklyPulse && (
          <Card className={`glass-card mb-8 ${theme === 'light' ? 'bg-gradient-to-br from-green-50 to-emerald-50' : 'bg-gradient-to-br from-green-900/20 to-emerald-900/20'}`}>
            <div className="flex items-center space-x-2 mb-4">
              <Sparkles className="w-6 h-6 text-yellow-400" />
              <h2 className={`text-2xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>Your Weekly Pulse</h2>
            </div>
            {loadingPulse ? (
              <div className="flex justify-center py-8"><div className="spinner"></div></div>
            ) : (
              <div className="space-y-4">
                <p className={`leading-relaxed ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>{weeklyPulse.pulse}</p>
                <div className={`grid grid-cols-3 gap-4 pt-4 border-t ${theme === 'dark' ? 'border-white/10' : 'border-gray-200'}`}>
                  <div>
                    <div className="text-2xl font-bold text-green-500">{weeklyPulse.journal_count}</div>
                    <div className={`text-xs ${theme === 'dark' ? 'text-gray-500' : 'text-gray-600'}`}>Entries</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-green-500">{weeklyPulse.video_count}</div>
                    <div className={`text-xs ${theme === 'dark' ? 'text-gray-500' : 'text-gray-600'}`}>Videos</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-green-500">{weeklyPulse.practice_hours?.toFixed(1)}</div>
                    <div className={`text-xs ${theme === 'dark' ? 'text-gray-500' : 'text-gray-600'}`}>Hours</div>
                  </div>
                </div>
              </div>
            )}
          </Card>
        )}

        {/* My Coaches */}
        {myCoaches.length > 0 && (
          <Card className={`glass-card mb-8 ${theme === 'light' ? 'bg-white/95' : ''}`}>
            <h2 className={`text-2xl font-bold mb-4 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>My Coaches</h2>
            <div className="flex flex-wrap gap-3">
              {myCoaches.map((coach) => (
                <div key={coach.id} className={`px-4 py-2 rounded-full ${theme === 'dark' ? 'bg-purple-500/20 border border-purple-500/30' : 'bg-purple-100 border border-purple-300'}`}>
                  <span className={`font-medium ${theme === 'dark' ? 'text-purple-300' : 'text-purple-700'}`}>
                    {coach.name}
                  </span>
                </div>
              ))}
            </div>
          </Card>
        )}

        {(user?.cricclubs_id || editingCricClubsId) && (
          <Card className={`glass-card mb-8 ${theme === 'light' ? 'bg-white/95' : ''}`}>
            <div className="flex items-center justify-between mb-6">
              <h2 className={`text-2xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>CricClubs Performance</h2>
              <div className="flex items-center space-x-2">
                <Button onClick={() => setEditingCricClubsId(!editingCricClubsId)} variant="outline" size="sm">
                  {editingCricClubsId ? 'Cancel' : 'Edit ID'}
                </Button>
                {!editingCricClubsId && (
                  <Button onClick={syncCricClubsData} disabled={syncingCricClubs} variant="outline" size="sm">
                    <RefreshCw className={`w-4 h-4 mr-2 ${syncingCricClubs ? 'animate-spin' : ''}`} />
                    {syncingCricClubs ? 'Syncing...' : 'Sync'}
                  </Button>
                )}
              </div>
            </div>

            {editingCricClubsId ? (
              <div className="space-y-4">
                <div>
                  <label className={`text-sm font-medium mb-2 block ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                    CricClubs Player ID
                  </label>
                  <div className="flex gap-2">
                    <Input
                      value={cricclubsIdInput}
                      onChange={(e) => setCricclubsIdInput(e.target.value)}
                      placeholder="Enter your CricClubs player ID (e.g., 4254751)"
                      className={theme === 'dark' ? 'bg-white/10 border-white/20 text-white' : 'bg-white border-gray-300 text-gray-900'}
                    />
                    <Button 
                      onClick={updateCricClubsId}
                      className="bg-gradient-to-r from-green-500 to-emerald-600"
                    >
                      Save & Sync
                    </Button>
                  </div>
                  <p className={`text-xs mt-2 ${theme === 'dark' ? 'text-gray-500' : 'text-gray-600'}`}>
                    Find your ID in your CricClubs profile URL (playerId=XXXXX)
                  </p>
                </div>
              </div>
            ) : cricclubsStats ? (
              <div className="space-y-4">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className={`p-4 rounded-lg text-center ${theme === 'dark' ? 'bg-white/5' : 'bg-gray-100'}`}>
                    <div className="text-3xl font-bold text-blue-500">{cricclubsStats.stats.matches_played}</div>
                    <div className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>Matches</div>
                  </div>
                  <div className={`p-4 rounded-lg text-center ${theme === 'dark' ? 'bg-white/5' : 'bg-gray-100'}`}>
                    <div className="text-3xl font-bold text-green-500">{cricclubsStats.stats.total_runs}</div>
                    <div className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>Runs</div>
                  </div>
                  <div className={`p-4 rounded-lg text-center ${theme === 'dark' ? 'bg-white/5' : 'bg-gray-100'}`}>
                    <div className="text-3xl font-bold text-orange-500">{cricclubsStats.stats.batting_average}</div>
                    <div className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>Average</div>
                  </div>
                  <div className={`p-4 rounded-lg text-center ${theme === 'dark' ? 'bg-white/5' : 'bg-gray-100'}`}>
                    <div className="text-3xl font-bold text-purple-500">{cricclubsStats.stats.strike_rate}</div>
                    <div className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>SR</div>
                  </div>
                </div>

                <div className="grid grid-cols-3 md:grid-cols-6 gap-3">
                  <div className={`p-3 rounded text-center ${theme === 'dark' ? 'bg-white/5' : 'bg-gray-100'}`}>
                    <div className="text-xl font-bold gradient-text">{cricclubsStats.stats.fifties}</div>
                    <div className={`text-xs ${theme === 'dark' ? 'text-gray-500' : 'text-gray-600'}`}>50s</div>
                  </div>
                  <div className={`p-3 rounded text-center ${theme === 'dark' ? 'bg-white/5' : 'bg-gray-100'}`}>
                    <div className="text-xl font-bold gradient-text">{cricclubsStats.stats.centuries}</div>
                    <div className={`text-xs ${theme === 'dark' ? 'text-gray-500' : 'text-gray-600'}`}>100s</div>
                  </div>
                  <div className={`p-3 rounded text-center ${theme === 'dark' ? 'bg-white/5' : 'bg-gray-100'}`}>
                    <div className="text-xl font-bold gradient-text">{cricclubsStats.stats.wickets_taken}</div>
                    <div className={`text-xs ${theme === 'dark' ? 'text-gray-500' : 'text-gray-600'}`}>Wickets</div>
                  </div>
                  <div className={`p-3 rounded text-center ${theme === 'dark' ? 'bg-white/5' : 'bg-gray-100'}`}>
                    <div className="text-xl font-bold gradient-text">{cricclubsStats.stats.catches}</div>
                    <div className={`text-xs ${theme === 'dark' ? 'text-gray-500' : 'text-gray-600'}`}>Catches</div>
                  </div>
                  <div className={`p-3 rounded text-center ${theme === 'dark' ? 'bg-white/5' : 'bg-gray-100'}`}>
                    <div className="text-xl font-bold gradient-text">{cricclubsStats.stats.economy_rate || 'N/A'}</div>
                    <div className={`text-xs ${theme === 'dark' ? 'text-gray-500' : 'text-gray-600'}`}>Econ</div>
                  </div>
                  <div className={`p-3 rounded text-center ${theme === 'dark' ? 'bg-white/5' : 'bg-gray-100'}`}>
                    <div className="text-xl font-bold gradient-text">{cricclubsStats.stats.highest_score}</div>
                    <div className={`text-xs ${theme === 'dark' ? 'text-gray-500' : 'text-gray-600'}`}>High</div>
                  </div>
                </div>

                <div className={`text-xs ${theme === 'dark' ? 'text-gray-500' : 'text-gray-600'} text-right`}>
                  Last synced: {new Date(cricclubsStats.last_synced).toLocaleString()}
                </div>
              </div>
            ) : (
              <div className="text-center py-8">
                <p className={`mb-4 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>Click Sync to import your CricClubs stats</p>
                {user?.cricclubs_id ? (
                  <p className={`text-sm ${theme === 'dark' ? 'text-gray-500' : 'text-gray-600'}`}>ID: {user.cricclubs_id}</p>
                ) : (
                  <Button onClick={() => setEditingCricClubsId(true)} variant="outline">
                    Add CricClubs ID
                  </Button>
                )}
              </div>
            )}
          </Card>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <Card className={`glass-card cursor-pointer hover:scale-105 transition-transform ${theme === 'light' ? 'bg-white/95' : ''}`} onClick={() => navigate('/journal/new')}>
            <div className="text-center py-6">
              <div className="w-14 h-14 mx-auto mb-3 rounded-full bg-gradient-to-br from-blue-400 to-cyan-500 flex items-center justify-center">
                <BookOpen className="w-7 h-7 text-white" />
              </div>
              <h3 className={`font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>New Journal</h3>
              <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>Log session</p>
            </div>
          </Card>

          <Card className={`glass-card cursor-pointer hover:scale-105 transition-transform ${theme === 'light' ? 'bg-white/95' : ''}`} onClick={() => navigate('/videos/upload')}>
            <div className="text-center py-6">
              <div className="w-14 h-14 mx-auto mb-3 rounded-full bg-gradient-to-br from-purple-400 to-pink-500 flex items-center justify-center">
                <Video className="w-7 h-7 text-white" />
              </div>
              <h3 className={`font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>Upload Video</h3>
              <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>Add practice</p>
            </div>
          </Card>

          <Card className={`glass-card cursor-pointer hover:scale-105 transition-transform ${theme === 'light' ? 'bg-white/95' : ''}`} onClick={() => navigate('/find-coach')}>
            <div className="text-center py-6">
              <div className="w-14 h-14 mx-auto mb-3 rounded-full bg-gradient-to-br from-green-400 to-emerald-500 flex items-center justify-center">
                <Users className="w-7 h-7 text-white" />
              </div>
              <h3 className={`font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>Find Coach</h3>
              <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>Get coaching</p>
            </div>
          </Card>

          <Card className={`glass-card cursor-pointer hover:scale-105 transition-transform ${theme === 'light' ? 'bg-white/95' : ''}`} onClick={() => navigate('/goals')}>
            <div className="text-center py-6">
              <div className="w-14 h-14 mx-auto mb-3 rounded-full bg-gradient-to-br from-orange-400 to-red-500 flex items-center justify-center">
                <Target className="w-7 h-7 text-white" />
              </div>
              <h3 className={`font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>Goals</h3>
              <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>Track progress</p>
            </div>
          </Card>
        </div>

        <Card className={`glass-card ${theme === 'light' ? 'bg-white/95' : ''}`}>
          <div className="flex items-center justify-between mb-6">
            <h2 className={`text-2xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>Recent Journal Entries</h2>
            <Button onClick={() => navigate('/journal/timeline')}>View All</Button>
          </div>
          
          {recentJournals.length > 0 ? (
            <div className="space-y-3">
              <div className={`grid grid-cols-7 gap-4 p-3 rounded-lg font-bold ${theme === 'dark' ? 'bg-white/5 text-gray-300' : 'bg-gray-100 text-gray-700'}`}>
                <div>Date</div>
                <div>Type</div>
                <div>Duration</div>
                <div>Practiced</div>
                <div className="text-center">Mood</div>
                <div className="text-center">Coach</div>
                <div className="text-center">Actions</div>
              </div>
              {recentJournals.map((journal) => (
                <div 
                  key={journal.id}
                  className={`grid grid-cols-7 gap-4 p-3 rounded-lg ${theme === 'dark' ? 'bg-white/5 hover:bg-white/10' : 'bg-gray-50 hover:bg-gray-100'} transition-colors`}
                >
                  <div className={`font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                    {new Date(journal.entry_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                  </div>
                  <div>
                    <span className={`text-sm px-2 py-1 rounded ${theme === 'dark' ? 'bg-blue-500/20 text-blue-400' : 'bg-blue-100 text-blue-700'}`}>
                      {journal.session_type}
                    </span>
                  </div>
                  <div className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                    {journal.duration_minutes} min
                  </div>
                  <div className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-700'} truncate`}>
                    {journal.what_practiced}
                  </div>
                  <div className="text-center">
                    <span className={`px-2 py-1 rounded ${theme === 'dark' ? 'bg-green-500/20 text-green-400' : 'bg-green-100 text-green-700'}`}>
                      {journal.mood_rating}/10
                    </span>
                  </div>
                  <div className="text-center">
                    {journal.has_coach_feedback ? (
                      <span className={`text-xs px-2 py-1 rounded ${theme === 'dark' ? 'bg-green-500/20 text-green-400' : 'bg-green-100 text-green-700'}`}>
                        ✓ Reviewed
                      </span>
                    ) : (
                      <span className={`text-xs ${theme === 'dark' ? 'text-gray-500' : 'text-gray-400'}`}>
                        -
                      </span>
                    )}
                  </div>
                  <div className="text-center">
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => navigate(`/journal/${journal.id}`)}
                    >
                      View
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <p className={`${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'} mb-4`}>No journal entries yet</p>
              <Button onClick={() => navigate('/journal/new')}>
                <Plus className="w-4 h-4 mr-2" />
                Create First Entry
              </Button>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
